import React, { useEffect, useState } from 'react';
import Box, { BoxProps } from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';

import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';

import useSendEmail from 'Hooks/useSendContactUsEmail';
import { useNotify } from 'components/NotificationProvider';

import Form from '../../../ContactUs/components/Form';

interface ContactLandingProps {
  sx?: BoxProps['sx'];
}

interface ContactLandingFormData {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  message: string;
  captchaToken: string;
  website: string;
}

const ContactLanding = ({ sx = {} }: ContactLandingProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { sendEmail, isLoading, isSuccess, error } = useSendEmail();
  const { notify, notifyError } = useNotify();
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<ContactLandingFormData>({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    message: '',
    captchaToken: '',
    website: '',
  });

  const isFormFilled = () => {
    return (
      formData.firstName.trim() !== '' &&
      formData.lastName.trim() !== '' &&
      formData.email.trim() !== '' &&
      formData.captchaToken.trim() !== '' &&
      formData.website.trim() === ''
    );
  };

  const handleClose = (
    event: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  useEffect(() => {
    if (isSuccess) {
      setOpen(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        message: '',
        captchaToken: '',
        website: '',
      });
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isSuccess === false && error) {
      notifyError(error);
    }
  }, [isSuccess, error, notifyError]);

  const handleRequestQuote = async () => {
    const formFilled = isFormFilled();
    if (formFilled) {
      await sendEmail(formData);
    } else {
      notify(
        'Please fill out all required fields before requesting a quote.',
        'warning',
      );
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '5vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(4),
        ...sx,
      }}
    >
      <Helmet>
        <title>{t('topbar.pages.company.main')}</title>
      </Helmet>
      <Box
        component="img"
        src={'/assets/contact_bateryjka.png'}
        sx={{
          display: {
            xs: 'none',
            sm: 'none',
            lg: 'block',
          },
          position: 'absolute',
          right: '-10%',
          top: 'calc(100% + 30px)',
          width: '25%',
          transform: 'rotate(0deg) translateY(-50%)',
          backgroundImage: '/assets/conctact_bateryjka.png',
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
        }}
      />
      <Box>
        <Typography
          variant="h3"
          gutterBottom
          sx={{
            marginBottom: theme.spacing(4),
          }}
        >
          {t('contactLanding.getInTouch')}
        </Typography>
        <Box>
          <Form
            formData={formData}
            setFormData={setFormData}
            handleSubmit={handleRequestQuote}
            disabled={isLoading}
          />
        </Box>
      </Box>
      {/* Notyfikacja */}
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleClose}
          severity="success"
          sx={{ width: '100%', marginTop: '60px' }}
        >
          Request has been sent!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactLanding;
